import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import { ShieldCheck, CheckCircle, WarningCircle, XCircle, Spinner, Sparkle, ArrowClockwise, Lock, LockOpen, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const ADMIN_ROLES = ["PLATFORM_OWNER", "OWNER", "ADMIN", "STUDIO_ADMIN"];
const SEV = { critical: "#FF3366", high: "#FF3366", medium: "#FFB800", low: "#00E5FF" };
const TYPES = [
  { key: "episode", label: "Episodes", ep: "/episodes" },
  { key: "story", label: "Stories", ep: "/stories" },
  { key: "short", label: "Shorts", ep: "/shorts" },
  { key: "game", label: "Games", ep: "/games" },
];

function Verdict({ v, overridden }) {
  const map = {
    PASS: ["#00FF9D", "PASS"], REVIEW_REQUIRED: ["#FFB800", "REVIEW REQUIRED"],
    BLOCKED: ["#FF3366", "BLOCKED"], NOT_REVIEWED: ["#71717a", "NOT REVIEWED"], OVERRIDDEN: ["#8B5CF6", "OVERRIDDEN"],
  };
  const key = overridden && v !== "PASS" ? "OVERRIDDEN" : v;
  const [c, label] = map[key] || map.NOT_REVIEWED;
  return (
    <span data-testid={`verdict-${key.toLowerCase()}`} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide font-mono"
      style={{ color: c, background: `${c}1a`, borderColor: `${c}55` }}>{label}</span>
  );
}

export default function Safety() {
  const { active } = useStudio();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes((user?.role || "").toUpperCase());
  const [tab, setTab] = useState("episode");
  const [items, setItems] = useState(null);
  const [reviews, setReviews] = useState({}); // target_id -> active review
  const [busy, setBusy] = useState(null); // target_id being reviewed
  const [detail, setDetail] = useState(null); // open review detail
  const [reason, setReason] = useState("");
  const [fixing, setFixing] = useState(null); // finding_id
  const [approving, setApproving] = useState(false);

  const loadReviews = async () => {
    const { data } = await api.get("/safety/reviews");
    const map = {};
    (data.reviews || []).forEach((r) => { map[`${r.target_type}:${r.target_id}`] = r; });
    setReviews(map);
  };
  const loadItems = async (t) => {
    setItems(null);
    const cfg = TYPES.find((x) => x.key === t);
    const { data } = await api.get(cfg.ep);
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { loadReviews(); }, [active?.id]);
  useEffect(() => { loadItems(tab); }, [tab, active?.id]);

  const poll = (jobId, done) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data: job } = await api.get(`/safety/review-job/${jobId}`);
        if (job.status === "done") done(job);
        else if (job.status === "error") { toast.error(job.error || "Review failed"); done(null); }
        else if (Date.now() - started > 150000) { toast.error("Review timed out"); done(null); }
        else setTimeout(tick, 3000);
      } catch { toast.error("Review failed"); done(null); }
    };
    setTimeout(tick, 2500);
  };

  const runReview = async (it) => {
    setBusy(it.id);
    try {
      const { data } = await api.post("/safety/review", { target_type: tab, target_id: it.id });
      poll(data.job_id, (job) => {
        setBusy(null);
        if (job && job.review) { toast.success(`Safety verdict: ${job.verdict.replace(/_/g, " ")}`); loadReviews(); setDetail(job.review); }
      });
    } catch (e) { setBusy(null); toast.error(e.response?.data?.detail || "Could not start review"); }
  };

  const recheck = async (r) => {
    setBusy(r.target_id);
    try {
      const { data } = await api.post(`/safety/reviews/${r.id}/recheck`);
      poll(data.job_id, (job) => {
        setBusy(null);
        if (job && job.review) { toast.success(`Re-checked: ${job.verdict.replace(/_/g, " ")}`); loadReviews(); setDetail(job.review); }
      });
    } catch (e) { setBusy(null); toast.error(e.response?.data?.detail || "Re-check failed"); }
  };

  const safeFix = async (r, f) => {
    setFixing(f.id);
    try {
      const { data } = await api.post(`/safety/reviews/${r.id}/fix`, { finding_id: f.id });
      const updated = await api.get(`/safety/reviews/${r.id}`);
      setDetail(updated.data);
      toast.success("Safe correction suggested");
    } catch (e) { toast.error(e.response?.data?.detail || "Safe-fix failed"); }
    finally { setFixing(null); }
  };

  const override = async (r) => {
    try {
      await api.post(`/safety/reviews/${r.id}/override`, { reason });
      const updated = await api.get(`/safety/reviews/${r.id}`);
      setDetail(updated.data); setReason(""); loadReviews();
      toast.success("Override recorded");
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed"); }
  };

  const approve = async (r) => {
    setApproving(true);
    try {
      const { data } = await api.post("/safety/approve-production", { target_type: r.target_type, target_id: r.target_id });
      toast.success(`Approved for production${data.overridden ? " (override on file)" : ""}`);
      loadReviews(); loadItems(tab); setDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Approval blocked"); }
    finally { setApproving(false); }
  };

  return (
    <div>
      <PageHeader icon={ShieldCheck} title="Kids Safety Gate"
        subtitle="Every children's item must pass an AI safety review before final production approval. BLOCKED items cannot be approved unless an Owner/Admin records a documented override." />

      <div data-testid="safety-notice" className="mb-6 flex items-start gap-3 rounded-xl border border-[#FFB800]/30 bg-[#FFB800]/8 px-4 py-3">
        <WarningCircle size={20} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
        <p className="text-sm text-zinc-300">The safety verdict is computed from finding severities — the AI cannot self-declare a lenient PASS. Automated review assists your judgement; the Owner remains responsible for what ships to children.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`safety-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`rounded-full px-4 py-1.5 text-sm border ${tab === t.key ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{t.label}</button>
        ))}
      </div>

      {!items ? <Loading label="Loading content…" /> : items.length === 0 ? (
        <p className="text-sm text-zinc-500">No {TYPES.find((t) => t.key === tab).label.toLowerCase()} in this studio yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const r = reviews[`${tab}:${it.id}`];
            const title = it.title || it.hook || it.topic || "Untitled";
            return (
              <Panel key={it.id} data-testid={`safety-item-${it.id}`} className="flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-display text-base font-bold text-white line-clamp-2">{title}</h4>
                  <Verdict v={r ? r.verdict : "NOT_REVIEWED"} overridden={r && !!r.override} />
                </div>
                {it.production_approved && <Tag className="mt-2 text-[#00FF9D] border-[#00FF9D]/40">Production approved</Tag>}
                <div className="mt-auto pt-3 flex flex-wrap gap-2">
                  <button data-testid={`run-review-${it.id}`} onClick={() => runReview(it)} disabled={busy === it.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-magic px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-60">
                    {busy === it.id ? <Spinner size={14} className="animate-spin" /> : <ShieldCheck size={14} weight="fill" />} {r ? "Re-review" : "Run Safety Review"}
                  </button>
                  {r && <button data-testid={`open-review-${it.id}`} onClick={() => setDetail(r)} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#00E5FF]">View Report</button>}
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div data-testid="safety-detail" className="relative w-full max-w-lg glass h-full overflow-y-auto p-6 animate-fadeUp">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-display text-2xl font-black text-white">{detail.target_title}</h3>
                <div className="text-xs text-zinc-500 mt-1 capitalize">{detail.target_type} · reviewed by {detail.model_id}</div>
              </div>
              <button data-testid="close-safety-detail" onClick={() => setDetail(null)}><X size={22} className="text-zinc-400" /></button>
            </div>
            <div className="mb-4"><Verdict v={detail.verdict} overridden={!!detail.override} /></div>

            {detail.summary && <p className="text-sm text-zinc-300 mb-5 bg-black/20 border border-white/5 rounded-xl p-3">{detail.summary}</p>}

            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Findings ({(detail.findings || []).length})</div>
            {(detail.findings || []).length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-[#00FF9D] mb-5"><CheckCircle size={18} weight="fill" /> No safety concerns found.</div>
            ) : (
              <div className="space-y-3 mb-5">
                {detail.findings.map((f) => (
                  <div key={f.id} data-testid={`finding-${f.id}`} className="rounded-xl border border-white/8 bg-black/20 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase font-mono px-2 py-0.5 rounded-full" style={{ color: SEV[f.severity], background: `${SEV[f.severity]}1a` }}>{f.severity}</span>
                      <Tag className="text-[10px] capitalize">{f.category.replace(/_/g, " ")}</Tag>
                    </div>
                    <div className="text-sm text-white font-medium">{f.section}</div>
                    <p className="text-xs text-zinc-400 mt-1"><b className="text-zinc-300">Concern:</b> {f.reason}</p>
                    {f.suggestion && <p className="text-xs text-zinc-400 mt-1"><b className="text-zinc-300">Suggested:</b> {f.suggestion}</p>}
                    {f.correction ? (
                      <div className="mt-2 rounded-lg border border-[#00FF9D]/30 bg-[#00FF9D]/8 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#00FF9D] mb-1">AI safe correction</div>
                        <p className="text-xs text-zinc-200 whitespace-pre-wrap">{f.correction.corrected_text}</p>
                        {f.correction.explanation && <p className="text-[11px] text-zinc-500 mt-1">{f.correction.explanation}</p>}
                      </div>
                    ) : (
                      <button data-testid={`fix-${f.id}`} onClick={() => safeFix(detail, f)} disabled={fixing === f.id}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#8B5CF6]/40 text-[#8B5CF6] px-3 py-1 text-xs disabled:opacity-60">
                        {fixing === f.id ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} AI Safe-Fix
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {detail.override && (
              <div data-testid="override-record" className="mb-5 rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 p-3">
                <div className="flex items-center gap-2 text-[#8B5CF6] text-xs font-bold uppercase tracking-wider mb-1"><LockOpen size={14} weight="fill" /> Override on file</div>
                <p className="text-xs text-zinc-300">{detail.override.reason}</p>
                <p className="text-[11px] text-zinc-500 mt-1">by {detail.override.by} · {new Date(detail.override.at).toLocaleString()}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              <button data-testid="recheck-btn" onClick={() => recheck(detail)} disabled={busy === detail.target_id}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-200 disabled:opacity-60">
                {busy === detail.target_id ? <Spinner size={13} className="animate-spin" /> : <ArrowClockwise size={13} />} Re-check
              </button>
              <button data-testid="approve-production-btn" onClick={() => approve(detail)} disabled={approving}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D] px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
                {approving ? <Spinner size={13} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />} Approve for Production
              </button>
            </div>

            {detail.verdict !== "PASS" && !detail.override && (
              isAdmin ? (
                <div data-testid="override-panel" className="rounded-xl border border-[#FF3366]/30 bg-[#FF3366]/8 p-3">
                  <div className="flex items-center gap-2 text-[#FF3366] text-xs font-bold uppercase tracking-wider mb-2"><Lock size={14} weight="fill" /> Owner/Admin override</div>
                  <p className="text-[11px] text-zinc-400 mb-2">Overriding a non-PASS verdict is logged. A documented reason is mandatory.</p>
                  <textarea data-testid="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="Why is it acceptable to proceed despite the safety verdict? (min 8 chars)"
                    className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white outline-none resize-none" />
                  <button data-testid="submit-override" onClick={() => override(detail)} disabled={reason.trim().length < 8}
                    className="mt-2 rounded-full bg-[#FF3366] px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Record Override</button>
                </div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-zinc-400 flex items-center gap-2">
                  <Lock size={14} /> Only an Owner/Admin can override a non-PASS safety verdict.
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
